import { CONSTRUCTION_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import type { AssessmentDetailStep1 } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step1'
import type {
  AssessmentDetailStep2NeighboringStateTypes,
  AssessmentDetailStep2FallingStateTypes,
  AssessmentDetailWoodStep2Neighboring,
  AssessmentDetailWoodStep2Falling,
  AssessmentDetailSteelStep2Neighboring,
  AssessmentDetailSteelStep2Falling,
  AssessmentDetailRcStep2Neighboring,
  AssessmentDetailRcStep2Falling
} from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step2'
import type { ConstructionType } from '@/types/accessor/api/emergencyInspectionResult/assessmentInfo/constructionType'
import {
  convertStringToNumber,
  calculationStep1Result,
  calculationNeighboringWood,
  calculationFallingWood,
  determineOverallAssessmentResult,
  calculationNeighboringSteel,
  calculationFallingSteel,
  calculationNeighboringReinforced,
  calculationFallingReinforced
} from './calculationHelperFuncs'
import { OVERALL_RESULT_VALUES } from '@/constants/accessor/api/emergencyInspectionResult/emergencyAssessment/overallResult'
import type { OverallResult } from '@/types/accessor/api/emergencyInspectionResult/emergencyAssessment/overallResult'
import { RISK_RANK } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/common/riskRank'
import { GlobalError } from '@/utils/globalError/GlobalError'
import { ERROR_CODES } from '@/constants/error/errorCode'

/**
 * 判定結果算出
 * 家屋種別で計算切り替え
 * @param constructionType 家屋構造種別
 * @param step1 step1入力値
 * @param step2 step2入力値
 * @returns 判定結果
 */
export const calculationOverallResult = <T extends ConstructionType>(
  constructionType: ConstructionType,
  step1: AssessmentDetailStep1,
  step2: {
    neighboring: AssessmentDetailStep2NeighboringStateTypes<T>
    falling: AssessmentDetailStep2FallingStateTypes<T>
  }
): OverallResult => {
  const exteriorRiskType = convertStringToNumber(
    step1.exterior.exteriorRiskType == null ? RISK_RANK.UNSELECTED : step1.exterior.exteriorRiskType
  )
  const step1Result = calculationStep1Result(exteriorRiskType)

  if (step1Result === Number(OVERALL_RESULT_VALUES.RANK_C)) {
    return OVERALL_RESULT_VALUES.RANK_C as unknown as OverallResult
  }

  switch (constructionType) {
    case CONSTRUCTION_TYPE.WOOD: {
      const neighboring = step2.neighboring as AssessmentDetailWoodStep2Neighboring
      const neighboringRiskRankList = [
        neighboring.surrounding.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.surrounding.riskRank,
        neighboring.differentialSettlement.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.differentialSettlement.riskRank,
        neighboring.basics.riskRank == null ? RISK_RANK.UNSELECTED : neighboring.basics.riskRank,
        neighboring.firstFloorSlope.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.firstFloorSlope.riskRank,
        neighboring.wall.riskRank == null ? RISK_RANK.UNSELECTED : neighboring.wall.riskRank,
        neighboring.corrosionOrTermite.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.corrosionOrTermite.riskRank
      ]

      const neighboringTargetList = neighboringRiskRankList.map(item => convertStringToNumber(item))
      const neighboringResult = calculationNeighboringWood(neighboringTargetList)

      const falling = step2.falling as AssessmentDetailWoodStep2Falling
      const fallingRiskRankList = [
        falling.roofTile.riskRank == null ? RISK_RANK.UNSELECTED : falling.roofTile.riskRank,
        falling.windowFrame.riskRank == null ? RISK_RANK.UNSELECTED : falling.windowFrame.riskRank,
        falling.exteriorWetMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.exteriorWetMaterial.riskRank,
        falling.exteriorDryMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.exteriorDryMaterial.riskRank,
        falling.signboard.riskRank == null ? RISK_RANK.UNSELECTED : falling.signboard.riskRank,
        falling.outdoorStairs.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.outdoorStairs.riskRank,
        falling.otherItem.riskRank == null ? RISK_RANK.UNSELECTED : falling.otherItem.riskRank
      ]
      const fallingTargetList = fallingRiskRankList.map(item => convertStringToNumber(item))
      const fallingResult = calculationFallingWood(fallingTargetList)
      return determineOverallAssessmentResult(
        step1Result,
        neighboringResult,
        fallingResult
      ) as unknown as OverallResult
    }
    case CONSTRUCTION_TYPE.STEEL: {
      const neighboring = step2.neighboring as AssessmentDetailSteelStep2Neighboring
      const neighboringRiskRankList = [
        neighboring.surrounding.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.surrounding.riskRank,
        neighboring.settlementSlopeSteel.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.settlementSlopeSteel.riskRank,
        neighboring.slope.riskRank == null ? RISK_RANK.UNSELECTED : neighboring.slope.riskRank,
        neighboring.buckling.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.buckling.riskRank,
        neighboring.brace.riskRank == null ? RISK_RANK.UNSELECTED : neighboring.brace.riskRank,
        neighboring.pillarBeamJoint.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.pillarBeamJoint.riskRank,
        neighboring.pillarBase.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.pillarBase.riskRank,
        neighboring.corrosion.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.corrosion.riskRank
      ]

      const neighboringTargetList = neighboringRiskRankList.map(item => convertStringToNumber(item))
      const neighboringResult = calculationNeighboringSteel(neighboringTargetList)

      const falling = step2.falling as AssessmentDetailSteelStep2Falling
      const fallingRiskRankList = [
        falling.roofMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.roofMaterial.riskRank,
        falling.windowFrame.riskRank == null ? RISK_RANK.UNSELECTED : falling.windowFrame.riskRank,
        falling.exteriorWetMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.exteriorWetMaterial.riskRank,
        falling.exteriorDryMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.exteriorDryMaterial.riskRank,
        falling.signboard.riskRank == null ? RISK_RANK.UNSELECTED : falling.signboard.riskRank,
        falling.outdoorStairs.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.outdoorStairs.riskRank,
        falling.otherItem.riskRank == null ? RISK_RANK.UNSELECTED : falling.otherItem.riskRank
      ]

      const fallingTargetList = fallingRiskRankList.map(item => convertStringToNumber(item))
      const fallingResult = calculationFallingSteel(fallingTargetList)

      return determineOverallAssessmentResult(
        step1Result,
        neighboringResult,
        fallingResult
      ) as unknown as OverallResult
    }
    case CONSTRUCTION_TYPE.RC: {
      const neighboring = step2.neighboring as AssessmentDetailRcStep2Neighboring
      const partsDamageGte3 = convertStringToNumber(
        neighboring.partsDamageGte3.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.partsDamageGte3.riskRank
      )
      const neighboringRiskRankList = [
        neighboring.surrounding.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.surrounding.riskRank,
        neighboring.groundDestructionSettlement.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.groundDestructionSettlement.riskRank,
        neighboring.settlementSlope.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.settlementSlope.riskRank,
        neighboring.pillarDamage5.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.pillarDamage5.riskRank,
        neighboring.pillarDamage4.riskRank == null
          ? RISK_RANK.UNSELECTED
          : neighboring.pillarDamage4.riskRank
      ]
      const neighboringTargetList = neighboringRiskRankList.map(item => convertStringToNumber(item))
      const neighboringResult = calculationNeighboringReinforced(
        partsDamageGte3,
        neighboringTargetList
      )

      const falling = step2.falling as AssessmentDetailRcStep2Falling
      const fallingRiskRankList = [
        falling.windowFrame.riskRank == null ? RISK_RANK.UNSELECTED : falling.windowFrame.riskRank,
        falling.exteriorWetMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.exteriorWetMaterial.riskRank,
        falling.exteriorDryMaterial.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.exteriorDryMaterial.riskRank,
        falling.signboard.riskRank == null ? RISK_RANK.UNSELECTED : falling.signboard.riskRank,
        falling.outdoorStairs.riskRank == null
          ? RISK_RANK.UNSELECTED
          : falling.outdoorStairs.riskRank,
        falling.otherItem.riskRank == null ? RISK_RANK.UNSELECTED : falling.otherItem.riskRank
      ]

      const fallingTargetList = fallingRiskRankList.map(item => convertStringToNumber(item))
      const fallingResult = calculationFallingReinforced(fallingTargetList)

      return determineOverallAssessmentResult(
        step1Result,
        neighboringResult,
        fallingResult
      ) as unknown as OverallResult
    }
    default: {
      throw new GlobalError([ERROR_CODES.STEP2_CONSTRUCTION_TYPE_UNSELECTED])
    }
  }
}
